/**
 * Product Card Swatches
 *
 * Handles product card swatch interactions for switching between
 * product variants/colors within a product grouping.
 *
 * This file is shared between:
 * - blocks/_product-card-swatches.liquid (native product cards)
 * - sections/main-collection.liquid (Searchspring product cards)
 */

const SELECTORS = {
  swatch: '.product-card-swatch',
  swatchActive: '.product-card-swatch--active',
  swatchContainer: '.product-card-swatches',
  cardGallery: '.card-gallery',
  productLink: '.product-card__link, a[ref="productCardLink"]',
  cardGalleryLink: 'a[ref="cardGalleryLink"]',
  productTitle: '.product-card__title, [class*="product-title"]',
  galleryImages: '.card-gallery img, slideshow-component img',
  mediaData: 'script[data-product-media]',
  slideshow: 'slideshow-component',
  price: 'product-price',
  sku: 'product-sku-component',
  quickAdd: 'quick-add-component',
  quickAddTemplate: '[data-quick-add-template]',
  variantInput: 'input[name="id"]',
};

const ACTIVE_CLASS = 'product-card-swatch--active';
const SECTION_ID = 'section-rendering-product-card';

/**
 * Image widths for responsive srcset generation.
 * These align with Shopify's smart default widths used by the image_tag filter.
 * For product cards, we cap at 832px (sufficient for 2x retina on ~400px cards).
 * @see https://shopify.dev/docs/api/liquid/filters/image_tag
 */
const IMAGE_WIDTHS = [165, 360, 535, 750, 832];
const DEFAULT_IMAGE_WIDTH = 535; // Fallback src width (~2x typical card size)
const PRELOAD_IMAGE_TIMEOUT_MS = 3000;
const PREFETCH_HOVER_DELAY_MS = 175;
const LOADING_STATE_DEFER_MS = 150;
const PREFETCH_CACHE_MAX = 10;

/**
 * Handles product card swatch interactions for switching between
 * product variants/colors within a product grouping.
 */
class ProductCardSwatches {
  /** @type {HTMLElement} */
  container;
  /** @type {HTMLElement} */
  productCard;
  /** @type {AbortController | null} */
  abortController = null;
  /** @type {AbortController | null} */
  prefetchAbortController = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  prefetchTimeoutId = null;
  /** @type {Map<string, HTMLElement>} */
  prefetchCache = new Map();

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    const productCard = /** @type {HTMLElement | null} */ (container.closest('product-card'));

    if (!productCard) {
      console.warn('ProductCardSwatches: Parent product-card element not found');
      this.productCard = container; // unused; instance exits before bindEvents
      return;
    }

    this.productCard = productCard;
    this.bindEvents();
  }

  /**
   * Binds click and pointer events to swatch buttons using event delegation.
   */
  bindEvents() {
    this.container.addEventListener('click', (e) => {
      const target = /** @type {Element | null} */ (e.target);
      const button = /** @type {HTMLButtonElement | null} */ (target?.closest(SELECTORS.swatch));
      if (button && !button.disabled) {
        this.handleSwatchClick(e, button);
      }
    });

    this.container.addEventListener('pointerenter', this.#handlePointerEnter, true);
    this.container.addEventListener('pointerleave', this.#handlePointerLeave, true);
  }

  /**
   * Cancel any in-flight prefetch and clear the hover delay timer.
   */
  #cancelPrefetch() {
    if (this.prefetchTimeoutId != null) {
      clearTimeout(this.prefetchTimeoutId);
      this.prefetchTimeoutId = null;
    }
    if (this.prefetchAbortController) {
      this.prefetchAbortController.abort();
      this.prefetchAbortController = null;
    }
  }

  /**
   * @param {PointerEvent} e
   */
  #handlePointerEnter = (e) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      /** @type {Element | null} */ (e.target)?.closest(SELECTORS.swatch)
    );
    if (!button || button.disabled) return;

    const { productUrl } = button.dataset;
    if (!productUrl) return;

    this.#cancelPrefetch();
    this.prefetchTimeoutId = setTimeout(() => {
      this.prefetchTimeoutId = null;
      this.#prefetchCard(productUrl);
    }, PREFETCH_HOVER_DELAY_MS);
  };

  /**
   * @param {PointerEvent} e
   */
  #handlePointerLeave = (e) => {
    const button = /** @type {Element | null} */ (e.target)?.closest(SELECTORS.swatch);
    if (button) this.#cancelPrefetch();
  };

  /**
   * Prefetch section-rendered card and store in cache. Uses its own AbortController.
   * @param {string} productUrl
   */
  async #prefetchCard(productUrl) {
    if (this.prefetchCache.has(productUrl)) return;

    this.prefetchAbortController = new AbortController();
    const apiUrl = new URL(productUrl, window.location.origin);
    apiUrl.searchParams.set('section_id', SECTION_ID);

    try {
      const response = await fetch(apiUrl.toString(), {
        signal: this.prefetchAbortController.signal,
      });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newCard = doc.querySelector('product-card');
      if (!newCard) return;

      // Evict oldest if at capacity
      if (this.prefetchCache.size >= PREFETCH_CACHE_MAX) {
        const firstKey = this.prefetchCache.keys().next().value;
        if (firstKey != null) this.prefetchCache.delete(firstKey);
      }
      this.prefetchCache.set(productUrl, /** @type {HTMLElement} */ (newCard));

      // Fire-and-forget: warm the browser's image cache so the preloadImage
      // call on click resolves near-instantly instead of waiting for a cold fetch.
      this.#prefetchPrimaryImage(/** @type {HTMLElement} */ (newCard));
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Ignore other errors for prefetch
      }
    } finally {
      this.prefetchAbortController = null;
    }
  }

  /**
   * Preloads the primary image from a prefetched card into the browser cache.
   * Fire-and-forget — called after hover prefetch so the image is warm before the user clicks.
   * @param {HTMLElement} card
   */
  #prefetchPrimaryImage(card) {
    const mediaScript = card.querySelector(SELECTORS.mediaData);
    if (!mediaScript) return;

    let mediaArray;
    try {
      mediaArray = JSON.parse(mediaScript.textContent);
    } catch {
      return;
    }

    const baseSrc = mediaArray?.[0]?.preview_image?.src;
    if (!baseSrc) return;

    const img = new Image();
    img.srcset = this.buildSrcset(baseSrc);
    img.src = this.buildImageUrl(baseSrc, DEFAULT_IMAGE_WIDTH);
  }

  /**
   * Get section-rendered card: from cache if available, otherwise fetch.
   * @param {string} productUrl
   * @returns {Promise<HTMLElement>}
   */
  async getProductCard(productUrl) {
    const cached = this.prefetchCache.get(productUrl);
    if (cached) {
      this.prefetchCache.delete(productUrl);
      return cached;
    }
    return this.fetchProductCard(productUrl);
  }

  /**
   * Updates the visual active state of swatches.
   * @param {HTMLElement} activeButton - The button to mark as active
   */
  setActiveState(activeButton) {
    const currentActive = this.container.querySelector(SELECTORS.swatchActive);
    currentActive?.classList.remove(ACTIVE_CLASS);
    activeButton.classList.add(ACTIVE_CLASS);
  }

  /**
   * Shows or hides a loading state on the card gallery.
   * @param {boolean} isLoading - Whether to show or hide loading state
   */
  setLoadingState(isLoading) {
    const gallery = /** @type {HTMLElement | null} */ (
      this.productCard.querySelector(SELECTORS.cardGallery)
    );
    if (!gallery) return;

    gallery.style.opacity = isLoading ? '0.6' : '';
    if (isLoading) {
      gallery.style.transition = 'opacity 0.2s ease';
    }
  }

  /**
   * Fetches the new product card HTML from the Section Rendering API.
   * @param {string} productUrl - The product URL to fetch
   * @returns {Promise<HTMLElement>} The new product-card element
   */
  async fetchProductCard(productUrl) {
    // Cancel any pending request
    this.abortController?.abort();
    this.abortController = new AbortController();

    const apiUrl = new URL(productUrl, window.location.origin);
    apiUrl.searchParams.set('section_id', SECTION_ID);

    const response = await fetch(apiUrl.toString(), {
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product card: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newCard = doc.querySelector('product-card');

    if (!newCard) {
      throw new Error('Product card not found in response HTML');
    }

    return /** @type {HTMLElement} */ (newCard);
  }

  /**
   * Handles swatch button clicks.
   * Uses cached card if prefetched; defers loading state until 150ms so fast paths don't dim.
   * @param {Event} e - The click event
   * @param {HTMLElement} button - The clicked swatch button
   */
  async handleSwatchClick(e, button) {
    e.preventDefault();
    e.stopPropagation();

    const { productHandle, productUrl } = button.dataset;
    if (!productHandle || !productUrl) return;

    this.#cancelPrefetch();
    this.setActiveState(button);

    const loadingStateTimerId = setTimeout(() => {
      this.setLoadingState(true);
    }, LOADING_STATE_DEFER_MS);

    try {
      const newProductCard = await this.getProductCard(productUrl);
      this.abortController = null;
      await this.updateProductCard(newProductCard);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('ProductCardSwatches: Error updating product card:', error);
      }
    } finally {
      clearTimeout(loadingStateTimerId);
      this.setLoadingState(false);
    }
  }

  /**
   * Updates the product card link href and the card gallery link href.
   * @param {HTMLElement} newCard
   */
  updateLink(newCard) {
    const currentLink = /** @type {HTMLAnchorElement | null} */ (
      this.productCard.querySelector(SELECTORS.productLink)
    );
    const newLink = /** @type {HTMLAnchorElement | null} */ (
      newCard.querySelector(SELECTORS.productLink)
    );

    if (currentLink && newLink) {
      currentLink.href = newLink.href;
    }

    const currentGalleryLink = /** @type {HTMLAnchorElement | null} */ (
      this.productCard.querySelector(SELECTORS.cardGalleryLink)
    );
    const newGalleryLink = /** @type {HTMLAnchorElement | null} */ (
      newCard.querySelector(SELECTORS.cardGalleryLink)
    );

    // The fetched card may not include cardGalleryLink (minimal section render);
    // fall back to the product link URL which points to the same product page.
    const newGalleryHref =
      newGalleryLink instanceof HTMLAnchorElement
        ? newGalleryLink.href
        : newLink instanceof HTMLAnchorElement
          ? newLink.href
          : null;

    if (currentGalleryLink instanceof HTMLAnchorElement && newGalleryHref) {
      currentGalleryLink.href = newGalleryHref;
    }
  }

  /**
   * Updates all product card elements with data from the new card.
   * @param {HTMLElement} newCard - The new product-card element
   * @returns {Promise<void>}
   */
  async updateProductCard(newCard) {
    this.updateLink(newCard);
    await this.updateGalleryImages(newCard);
    this.updatePrice(newCard);
    this.updateSku(newCard);
    this.updateQuickAdd(newCard);
    this.updateTitle(newCard);
    this.updateProductId(newCard);
    this.updateSwatches(newCard);
  }

  /**
   * Preloads an image so it is in the browser cache before we swap the DOM.
   * Resolves when the image loads or after timeout to avoid blocking.
   * @param {string} src - Image URL to preload
   * @returns {Promise<void>}
   */
  preloadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      const timeoutId = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        resolve();
      }, PRELOAD_IMAGE_TIMEOUT_MS);
      img.onload = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      img.src = src;
    });
  }

  /**
   * Updates gallery images using the product media data.
   * Preloads the primary image before swapping to avoid flash.
   * @param {HTMLElement} newCard
   * @returns {Promise<void>}
   */
  async updateGalleryImages(newCard) {
    const mediaScript = newCard.querySelector(SELECTORS.mediaData);
    if (!mediaScript) return;

    let mediaArray;
    try {
      mediaArray = JSON.parse(mediaScript.textContent);
    } catch {
      console.warn('ProductCardSwatches: Failed to parse media data');
      return;
    }

    if (!mediaArray?.length) return;

    const galleryImages = /** @type {NodeListOf<HTMLImageElement>} */ (
      this.productCard.querySelectorAll(SELECTORS.galleryImages)
    );
    const imageUpdates = this.buildImageUpdates(galleryImages, mediaArray);
    if (imageUpdates.length === 0) return;

    // Preload the first image so it is in cache before we swap (avoids flash)
    const firstSrc = imageUpdates[0]?.src;
    if (!firstSrc) return;
    await this.preloadImage(firstSrc);

    // Batch DOM updates in a single animation frame
    requestAnimationFrame(() => {
      imageUpdates.forEach(({ img, src, srcset, alt, width }) => {
        // Update srcset first (before src) to let browser choose optimal size
        img.srcset = srcset;
        img.src = src;
        img.alt = alt;
        img.setAttribute('width', String(width));
      });

      this.resetSlideshow();
    });
  }

  /**
   * Builds an array of image update objects with optimized srcsets.
   * @param {NodeListOf<HTMLImageElement>} images - The current gallery images
   * @param {Array<any>} mediaArray - The new media data
   * @returns {{img: HTMLImageElement, src: string, srcset: string, alt: string, width: number}[]}
   */
  buildImageUpdates(images, mediaArray) {
    /** @type {{img: HTMLImageElement, src: string, srcset: string, alt: string, width: number}[]} */
    const updates = [];
    const maxMediaIndex = Math.min(mediaArray.length - 1, 3);

    images.forEach((img, index) => {
      const mediaItem = mediaArray[Math.min(index, maxMediaIndex)];
      const previewSrc = mediaItem?.preview_image?.src;

      if (!previewSrc) return;

      // Generate srcset for responsive images
      const srcset = this.buildSrcset(previewSrc);

      // Use optimized default width for src (fallback)
      const src = this.buildImageUrl(previewSrc, DEFAULT_IMAGE_WIDTH);

      updates.push({
        img,
        src,
        srcset,
        alt: mediaItem.alt || img.alt,
        width: DEFAULT_IMAGE_WIDTH,
      });
    });

    return updates;
  }

  /**
   * Builds a srcset string for responsive images.
   * Follows Shopify's image_tag filter pattern for srcset generation.
   * @param {string} baseSrc - The base image URL (from media preview_image.src)
   * @returns {string} The srcset string with multiple width descriptors
   * @see https://shopify.dev/docs/api/liquid/filters/image_tag#image_tag-widths
   */
  buildSrcset(baseSrc) {
    return IMAGE_WIDTHS.map((width) => `${this.buildImageUrl(baseSrc, width)} ${width}w`).join(
      ', ',
    );
  }

  /**
   * Builds a Shopify CDN image URL with the specified width.
   * Follows the same URL structure as Shopify's image_url Liquid filter.
   * @param {string} baseSrc - The base image URL (may be protocol-relative)
   * @param {number} width - The desired width in pixels (max 5760)
   * @returns {string} The full CDN URL with width parameter
   * @see https://shopify.dev/docs/api/liquid/filters/image_url
   * @example
   *   buildImageUrl('//cdn.shopify.com/s/.../image.jpg?v=123', 400)
   *   // Returns: '//cdn.shopify.com/s/.../image.jpg?v=123&width=400'
   */
  buildImageUrl(baseSrc, width) {
    // Remove any existing width or height parameters to avoid conflicts
    // Shopify CDN uses: ?v=timestamp&width=XXX or ?width=XXX&v=timestamp
    let cleanSrc = baseSrc
      .replace(/([?&])width=\d+/g, '$1')
      .replace(/([?&])height=\d+/g, '$1')
      .replace(/[?&]+$/, '') // Remove trailing ? or &
      .replace(/\?&/g, '?') // Fix ?& sequences
      .replace(/&&+/g, '&'); // Fix && sequences

    const separator = cleanSrc.includes('?') ? '&' : '?';
    return `${cleanSrc}${separator}width=${width}`;
  }

  /**
   * Resets the slideshow component to the first slide.
   */
  resetSlideshow() {
    const slideshow = /** @type {any} */ (this.productCard.querySelector(SELECTORS.slideshow));
    if (!slideshow) return;

    if (typeof slideshow.select === 'function') {
      slideshow.select(0, null, { animate: false });
    } else if (slideshow.current !== undefined) {
      slideshow.current = 0;
    }
  }

  /**
   * Updates the price component.
   * @param {HTMLElement} newCard
   */
  updatePrice(newCard) {
    const currentPrice = /** @type {HTMLElement | null} */ (
      this.productCard.querySelector(SELECTORS.price)
    );
    const newPrice = /** @type {HTMLElement | null} */ (newCard.querySelector(SELECTORS.price));

    if (currentPrice && newPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
  }

  /**
   * Updates the SKU component.
   * @param {HTMLElement} newCard
   */
  updateSku(newCard) {
    const currentSku = /** @type {HTMLElement | null} */ (
      this.productCard.querySelector(SELECTORS.sku)
    );
    const newSku = /** @type {HTMLElement | null} */ (newCard.querySelector(SELECTORS.sku));

    if (currentSku && newSku) {
      currentSku.innerHTML = newSku.innerHTML;
      currentSku.style.display = newSku.style.display;
    }
  }

  /**
   * Updates the quick-add component.
   * @param {HTMLElement} newCard
   */
  updateQuickAdd(newCard) {
    const quickAdd = /** @type {HTMLElement | null} */ (
      this.productCard.querySelector(SELECTORS.quickAdd)
    );
    const newQuickAdd = /** @type {HTMLElement | null} */ (
      newCard.querySelector(SELECTORS.quickAdd)
    );
    const newTemplate = newQuickAdd?.querySelector(SELECTORS.quickAddTemplate);

    if (!quickAdd || !newQuickAdd || !newTemplate) return;

    // Update data attributes
    const { productId, productTitle, sectionId } = newQuickAdd.dataset;
    if (productId) quickAdd.dataset.productId = productId;
    if (productTitle) quickAdd.dataset.productTitle = productTitle;
    if (sectionId) quickAdd.dataset.sectionId = sectionId;

    // Replace template element
    const currentTemplate = quickAdd.querySelector(SELECTORS.quickAddTemplate);
    if (currentTemplate) {
      currentTemplate.replaceWith(newTemplate.cloneNode(true));
    }

    // Update variant input
    this.updateVariantInput(quickAdd, newQuickAdd);
  }

  /**
   * Updates the variant input within the quick-add form.
   * @param {HTMLElement} quickAdd
   * @param {HTMLElement} newQuickAdd
   */
  updateVariantInput(quickAdd, newQuickAdd) {
    const currentInput = /** @type {HTMLInputElement | null} */ (
      quickAdd.querySelector(`form ${SELECTORS.variantInput}`)
    );
    const newInput = /** @type {HTMLInputElement | null} */ (
      newQuickAdd.querySelector(`form ${SELECTORS.variantInput}`)
    );

    if (currentInput && newInput) {
      currentInput.value = newInput.value;
      currentInput.disabled = newInput.disabled;
    }
  }

  /**
   * Updates the product title.
   * @param {HTMLElement} newCard
   */
  updateTitle(newCard) {
    const currentTitle = /** @type {HTMLElement | null} */ (
      this.productCard.querySelector(SELECTORS.productTitle)
    );
    const newTitle = /** @type {HTMLElement | null} */ (
      newCard.querySelector(SELECTORS.productTitle)
    );

    if (currentTitle && newTitle) {
      currentTitle.innerHTML = newTitle.innerHTML;
    }
  }

  /**
   * Updates the product ID data attribute.
   * @param {HTMLElement} newCard
   */
  updateProductId(newCard) {
    const { productId } = newCard.dataset;
    if (productId) {
      this.productCard.dataset.productId = productId;
    }
  }

  /**
   * Updates the swatches container with the new card's swatches.
   * The fetched card has the correct swatch order (active swatch first),
   * so we replace the entire container to maintain proper ordering.
   * @param {HTMLElement} newCard
   */
  updateSwatches(newCard) {
    const newSwatchContainer = /** @type {HTMLElement | null} */ (
      newCard.querySelector(SELECTORS.swatchContainer)
    );

    if (!newSwatchContainer) return;

    // Replace the entire swatches container HTML to get the correct order
    this.container.innerHTML = newSwatchContainer.innerHTML;

    // Update data attributes for limits
    if (newSwatchContainer.dataset.mobileLimit) {
      this.container.dataset.mobileLimit = newSwatchContainer.dataset.mobileLimit;
    }
    if (newSwatchContainer.dataset.desktopLimit) {
      this.container.dataset.desktopLimit = newSwatchContainer.dataset.desktopLimit;
    }
    if (newSwatchContainer.dataset.cardId) {
      this.container.dataset.cardId = newSwatchContainer.dataset.cardId;
    }
  }
}

/**
 * Initializes ProductCardSwatches on all uninitialized containers.
 * Uses a Set to track initialization and avoid duplicate processing.
 */
const initializedContainers = new WeakSet();

function initProductCardSwatches() {
  const containers = document.querySelectorAll(SELECTORS.swatchContainer);

  containers.forEach((container) => {
    const el = /** @type {HTMLElement} */ (container);
    // Skip if already initialized (using WeakSet for memory efficiency)
    if (initializedContainers.has(el)) return;

    initializedContainers.add(el);
    el.dataset.initialized = 'true';
    new ProductCardSwatches(el);
  });
}

// Initialize on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardSwatches, { once: true });
} else {
  initProductCardSwatches();
}

// Listen for Searchspring events (more efficient than MutationObserver on body)
// These events fire when new product cards are dynamically added
document.addEventListener('ss:products-rendered', initProductCardSwatches);
document.addEventListener('ss:cards-initialized', initProductCardSwatches);

// Targeted MutationObserver - only observes product grid containers
// This is more performant than observing the entire document body
const observeProductGrids = () => {
  const grids = document.querySelectorAll(
    '.product-grid, [ref="grid"], results-list, searchspring-results-list',
  );

  if (grids.length === 0) return;

  const observer = new MutationObserver((mutations) => {
    // Only run if there are actual added nodes with swatch containers
    const hasNewSwatches = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;
        const el = /** @type {Element} */ (node);
        return (
          el.matches?.(SELECTORS.swatchContainer) || !!el.querySelector?.(SELECTORS.swatchContainer)
        );
      }),
    );

    if (hasNewSwatches) {
      initProductCardSwatches();
    }
  });

  grids.forEach((grid) => {
    observer.observe(grid, { childList: true, subtree: true });
  });
};

// Set up targeted observers once DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeProductGrids, { once: true });
} else {
  // Use requestIdleCallback for non-critical setup
  if ('requestIdleCallback' in window) {
    requestIdleCallback(observeProductGrids);
  } else {
    setTimeout(observeProductGrids, 100);
  }
}
